import React, { FC } from 'react'
import {
  ScreenReaderOnly,
  Text,
  Flex,
  Box,
  Pill,
} from '@moonpig/launchpad-components'
import { system as s } from '@moonpig/launchpad-system'
import { styled } from '@moonpig/launchpad-utils'
import { IconSystemChevronRight } from '@moonpig/launchpad-assets'
import { Checkbox } from '@moonpig/launchpad-forms'
import { colorValue } from '@moonpig/launchpad-theme'
import { Item } from '@radix-ui/react-dropdown-menu'
import { useIsMobile } from '@moonpig/web-shared-utils'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import {
  Filter,
  FilterCategory as FilterCategoryType,
  FilterItem,
} from '../../../../services/types/services'
import { SelectedFiltersList } from './SelectedFiltersList'
import { useFindLocaleText } from '../../../../text-localisation'
import { FILTERS_MENU_HEADER_HEIGHT_PX } from '../../constants'
import { mapFilterItem } from '../../../../utils/GalleryFiltering/mappings'
import { AccordionContent, AccordionHeading } from '../AccordionMenu'

const ICON_SIZE = 24

const StyledLabel = styled(Text)`
  display: block;
  ${s({
    textAlign: 'left',
    typography: { xs: 'typeMobileDisplay05', md: 'typeDesktopBodyLabel' },
  })}
`

const StyledAccordionLabel = styled.label<{ hasSelectedChildren: boolean }>`
  pointer-events: none;
  ${({ hasSelectedChildren }) =>
    s({
      typography: 'typeBodyText',
      color: 'colorTextBody',
      fontWeight: hasSelectedChildren
        ? 'bold'
        : /* istanbul ignore next */ 'normal',
    })}
`

const StyledButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: ${FILTERS_MENU_HEADER_HEIGHT_PX};
  ${s({
    py: 5,
    px: 6,
    borderBottom: 1,
    borderColor: 'colorBorder04',
    color: 'colorTextLabel',
  })}

  &:hover {
    ${s({
      bgcolor: 'colorBackground02',
      borderColor: 'colorBlack05',
    })}
  }

  &:focus,
  &:focus-visible {
    outline-color: ${colorValue('colorInteractionFocusIndicator')};
    ${s({
      bgcolor: 'colorBackground02',
      borderColor: 'colorBlack05',
      color: 'colorInteractionFocusIndicator',
    })}

    &:active {
      ${s({
        bgcolor: 'colorBackground03',
        borderColor: 'colorBlack10',
      })}
    }
  }
`

const StyledButtonTextWrapper = styled(Flex)`
  flex-direction: column;
  flex: 5;
  overflow: hidden;
  justify-content: space-between;
`

const StyledIconWrapper = styled(Flex)`
  flex: 1;
  justify-content: flex-end;
`

const StyledChevronRightIcon = styled(IconSystemChevronRight)`
  flex-shrink: 0;
`

const StyledDropdownItem = styled(Item)`
  &:focus-visible {
    outline: none;
    ${s({
      bgcolor: 'colorBackground02',
      borderColor: 'colorBlack05',
      color: 'colorInteractionFocusIndicator',
    })}
  }
`

type Props = {
  filter: FilterCategoryType
  onClick: (filter: FilterCategoryType | FilterItem, select?: boolean) => void
  level: number
  isDropdown?: boolean
}

export const FilterCategory: FC<Props> = ({
  filter,
  level,
  onClick,
  isDropdown = false,
}) => {
  const [accordionExpanded, setAccordionExpanded] = React.useState(false)
  const localiseText = useFindLocaleText()
  const isMobile = useIsMobile()

  const useAgeSelectionUx =
    useExperiment('search-use-age-selection-ux', {
      fallback: 'control',
    })?.toLowerCase() === 'enabled'

  const handleCheckboxClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (filter.isSelected || accordionExpanded) {
      e.stopPropagation()
      return
    }
    setAccordionExpanded(true)
  }

  const renderButton = () => (
    <StyledButton
      data-testid="filter-category"
      onClick={() => onClick(filter)}
      key={filter.id}
      role="tab"
      aria-controls={`${filter.id}-filter-category-panel`}
    >
      <StyledButtonTextWrapper>
        <StyledLabel>{filter.label}</StyledLabel>
        <ScreenReaderOnly>
          {localiseText('find.filters_menu_panel.open', filter.label)}
        </ScreenReaderOnly>
        <SelectedFiltersList filter={filter} />
      </StyledButtonTextWrapper>
      {filter.isNew && useAgeSelectionUx && <Pill label="New" />}
      <StyledIconWrapper>
        <StyledChevronRightIcon width={ICON_SIZE} height={ICON_SIZE} />
      </StyledIconWrapper>
    </StyledButton>
  )

  if (level === 0) {
    return renderButton()
  }

  return (
    <AccordionContent
      heading={
        <AccordionHeading
          onClick={() => setAccordionExpanded(!accordionExpanded)}
        >
          <Flex flexDirection="row" alignItems="center">
            <Box onClick={handleCheckboxClick}>
              <Checkbox
                onChange={() => onClick(filter, !filter.isSelected)}
                checked={!!filter.isSelected}
                value={filter.id}
                id={filter.id}
                indeterminate={filter.hasSelectedChildren && !filter.isSelected}
              />
            </Box>
            <StyledAccordionLabel
              htmlFor={filter.id}
              hasSelectedChildren={filter.hasSelectedChildren}
            >
              {filter.label}
            </StyledAccordionLabel>
          </Flex>
        </AccordionHeading>
      }
      value={filter.label}
    >
      {filter.children?.map(filterItem =>
        isDropdown && !isMobile ? (
          <DesktopDropdownItem
            filterItem={filterItem}
            onClick={onClick}
            level={level}
          />
        ) : (
          mapFilterItem({
            filterItem,
            onClick,
            level: level + 1,
            indentation: 8,
          })
        ),
      )}
    </AccordionContent>
  )
}

const DesktopDropdownItem = ({
  filterItem,
  onClick,
  level,
}: {
  filterItem: FilterItem
  onClick: (filter: FilterItem, select?: boolean) => void
  level: number
}) => {
  /* istanbul ignore next */
  const getFilterItemKey = (filterItem as Filter).id || filterItem.label

  const handleSelect = (event: Event) => event.preventDefault()

  const handleKeyDown = (event: React.KeyboardEvent) => {
    /* istanbul ignore else */
    if (['Enter', ' '].includes(event.key)) {
      onClick(filterItem, !(filterItem as Filter).isSelected)
    }
  }

  return (
    <StyledDropdownItem
      data-testid={`quick-filter-dropdown-item-${getFilterItemKey}`}
      key={getFilterItemKey}
      onSelect={handleSelect}
      onKeyDown={handleKeyDown}
    >
      {mapFilterItem({
        filterItem,
        onClick,
        indentation: 8,
        level: level + 1,
      })}
    </StyledDropdownItem>
  )
}
